/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  SimpleGrid,
  Box,
  Text,
  Spinner,
  Select,
  Flex,
  Divider,
  Center,
  Td,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Badge,
  Button,
  HStack,
  Icon,
  VStack,
} from "@chakra-ui/react";
import { useSearch } from "../../context/SearchContext";
import { FaStore, FaBan, FaWallet, FaTruck, FaFlask } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import ResponsiveTable from "../../components/ResponsiveTable";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";
import UnifiedChart from "../../components/UnifiedChart";
import "../../../i18n";

export default function Dashboard() {
  const { t } = useTranslation();
  const { isArabic, colors, dir, textAlign, isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [latestTenants, setLatestTenants] = useState([]);
  const { query } = useSearch();

  const [filterPeriod, setFilterPeriod] = useState("30days");
  const baseUrl = (
    process.env.NODE_ENV === "development"
      ? "/api"
      : (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api")).replace(/\/+$/, "");


  const fetchData = async () => {
    try {
      const res = await fetch(`${baseUrl}/GetAllTenantFullDetails`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status} - ${res.statusText}`);
      }

      // 🔥 قراءة الرد كـ Text ثم محاولة تحويله إلى JSON
      let raw = await res.text();
      let data;

      try {
        data = JSON.parse(raw);
      } catch {
        console.error("❌ Cannot parse tenants response:");
        console.error("Response:", raw);
        throw new Error("Invalid response from API");
      }

      // 🔥 توحيد البيانات → دائمًا Array
      if (!Array.isArray(data)) {
        data = [data];
      }

      // 🔹 حساب الإحصائيات
      const active = data.filter((t) => t.IsActive === 1).length;
      const inactive = data.filter((t) => t.IsActive !== 1).length;

      const trial = data.filter(
        (t) =>
          (t.PlanType && t.PlanType.toLowerCase().includes("trial")) ||
          (t.EngPlanType && t.EngPlanType.toLowerCase().includes("trial")) ||
          t.Price === 0 ||
          t.Price === "0" ||
          (t.Price && t.Price.toString().toLowerCase() === "free")
      ).length;

      const deliveryCompanies = data.filter(
        (t) =>
          (t.BusinessName && t.BusinessName.toLowerCase().includes("delivery")) ||
          (t.Domain && t.Domain.toLowerCase().includes("delivery"))
      ).length;

      const totalRevenue = active * 100;

      // 🔥 آخر 5 متاجر مضافين
      const sortedTenants = data
        .sort((a, b) => new Date(b.CreatedAt || 0) - new Date(a.CreatedAt || 0))
        .slice(0, 5);

      setLatestTenants(sortedTenants);

      setStats({
        activeStores: active,
        inactiveStores: inactive,
        trialStores: trial,
        totalRevenue,
        deliveryCompanies,
      });

    } catch (err) {
      console.error("Error loading dashboard:", err);

      // منع الكراش — إعداد قيم افتراضية
      setStats({
        activeStores: 0,
        inactiveStores: 0,
        trialStores: 0,
        totalRevenue: 0,
        deliveryCompanies: 0,
      });
    }
  };
  useEffect(() => {
    fetchData();
  }, [filterPeriod]);


  if (!stats)
    return (
      <Center mt="100px">
        <Spinner size="xl" color={colors.mainColor} />
      </Center>
    );

  const getRevenue = () => {
    switch (filterPeriod) {
      case "7days":
        return stats.totalRevenue / 4;
      case "30days":
        return stats.totalRevenue;
      case "year":
        return stats.totalRevenue * 12;
      default:
        return stats.totalRevenue;
    }
  };

  const formatIQD = (value) =>
    isArabic
      ? `${value.toLocaleString("ar-IQ")} دينار عراقي`
      : `IQD ${value.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  const getChartData = () => {
    if (filterPeriod === "7days") {
      return Array.from({ length: 7 }, (_, i) => ({
        name: isArabic ? `اليوم ${i + 1}` : `Day ${i + 1}`,
        value: Math.floor(Math.random() * 500 + 500),
      }));
    } else if (filterPeriod === "30days") {
      return ["الأسبوع 1", "الأسبوع 2", "الأسبوع 3", "الأسبوع 4"].map(
        (w, i) => ({
          name: isArabic ? w : `Week ${i + 1}`,
          value: Math.floor(Math.random() * 2000 + 1500),
        })
      );
    } else {
      return [
        "يناير",
        "فبراير",
        "مارس",
        "أبريل",
        "مايو",
        "يونيو",
        "يوليو",
        "أغسطس",
        "سبتمبر",
        "أكتوبر",
        "نوفمبر",
        "ديسمبر",
      ].map((m, i) => ({
        name: isArabic ? m : `Month ${i + 1}`,
        value: Math.floor(Math.random() * 4000 + 2000),
      }));
    }
  };


  const filteredTenants = latestTenants.filter((tenant) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      tenant.TenantName?.toLowerCase().includes(q) ||
      tenant.EngTenantName?.toLowerCase().includes(q) ||
      tenant.Email?.toLowerCase().includes(q) ||
      tenant.SubDomain?.toLowerCase().includes(q)
    );
  });

  return (
    <PageContainer title={t("admin.dashboard")}>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap">

        <Select
          width={{ base: "100%", sm: "230px" }}
          value={filterPeriod}
          onChange={(e) => setFilterPeriod(e.target.value)}
          bg={colors.inputBg}
          color={colors.textColor}
          borderColor={colors.inputBorder}
          borderRadius="md"
          px={3}
          py={2}
          textAlign="center"
          _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
          _hover={{ borderColor: colors.mainColor }}
          _dark={{ bg: colors.inputBg, color: "white" }}
          sx={{
            "& option": {
              textAlign: "center",
              color: isDark ? "white" : colors.textColor,
            },
          }}
        >
          <option value="7days">
            {isArabic ? "آخر 7 أيام" : "Last 7 Days"}
          </option>
          <option value="30days">
            {isArabic ? "آخر 30 يوم" : "Last 30 Days"}
          </option>
          <option value="year">{isArabic ? "آخر سنة" : "Last Year"}</option>
        </Select>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        <UnifiedCard
          icon={FaStore}
          title={isArabic ? "عدد المتاجر النشطة" : "Active Stores"}
        >
          <Text mt={3} fontSize="2xl" fontWeight="bold" color={colors.textColor} textAlign="center">
            {stats.activeStores}
          </Text>
        </UnifiedCard>

        <UnifiedCard
          icon={FaBan}
          title={isArabic ? "عدد المتاجر غير المفعلة" : "Inactive Stores"}
        >
          <Text mt={3} fontSize="2xl" fontWeight="bold" color={colors.textColor} textAlign="center">
            {stats.inactiveStores}
          </Text>
        </UnifiedCard>

        <UnifiedCard
          icon={FaFlask}
          title={isArabic ? "المتاجر التجريبية" : "Trial Stores"}
        >
          <Text mt={3} fontSize="2xl" fontWeight="bold" color={colors.textColor} textAlign="center">
            {stats.trialStores}
          </Text>
        </UnifiedCard>

        <UnifiedCard
          icon={FaWallet}
          title={isArabic ? "إجمالي الإيرادات" : "Total Revenue"}
        >
          <Text mt={3} fontSize="xl" fontWeight="bold" color={colors.textColor} textAlign="center">
            {formatIQD(getRevenue())}
          </Text>
        </UnifiedCard>

        <UnifiedCard
          icon={FaTruck}
          title={isArabic ? "شركات التوصيل" : "Delivery Companies"}
        >
          <Text mt={3} fontSize="2xl" fontWeight="bold" color={colors.textColor} textAlign="center">
            {stats.deliveryCompanies}
          </Text>
        </UnifiedCard>
      </SimpleGrid>

      <Divider my={10} />

      <Box mb={10}>
        <UnifiedChart
          type="bar"
          data={getChartData()}
          dataKey="value"
          nameKey="name"
          height={400}
          gradient={true}
          title={isArabic ? "الإيرادات حسب المدة" : "Revenue by Period"}
        />
      </Box>

      <UnifiedCard mt={10} title={isArabic ? "أحدث المتاجر" : "Latest Stores"}>
        <ResponsiveTable
          columns={[
            { header: isArabic ? "الاسم" : "Name", sortKey: "EngTenantName" },
            { header: isArabic ? "البريد الإلكتروني" : "Email", sortKey: "Email" },
            { header: isArabic ? "النطاق" : "Domain", sortKey: "Domain" },
            { header: isArabic ? "تاريخ الإنشاء" : "Created At", sortKey: "CreatedAt" },
          ]}
          data={filteredTenants}
          searchable={true}
          sortable={true}
          renderRow={(tenant) => (
            <>
              <Td color={colors.textColor}>{tenant.EngTenantName || tenant.TenantName}</Td>
              <Td color={colors.textColor}>{tenant.Email}</Td>
              <Td
                color={colors.textColor}
                whiteSpace="nowrap"
                maxW="180px"
                overflow="hidden"
                textOverflow="ellipsis"
              >{tenant.SubDomain || "-"}
              </Td>
              <Td color={colors.textColor} textAlign={textAlign}>{tenant.CreatedAt?.split("T")[0]}</Td>
            </>
          )}
          renderCard={(tenant) => (
            <>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="bold" color={colors.mainColor} fontSize="lg">
                  {tenant.EngTenantName || tenant.TenantName}
                </Text>
                <Text fontSize="xs" color={colors.subText}>
                  {tenant.CreatedAt?.split("T")[0]}
                </Text>
              </Flex>
              <Divider mb={3} borderColor={colors.borderColor} opacity={0.4} />
              <Box>
                <Text fontSize="sm" color={colors.textColor} mb={1}>
                  <Text as="span" color={colors.mainColor} fontWeight="semibold">
                    {isArabic ? "البريد الإلكتروني: " : "Email: "}
                  </Text>
                  {tenant.Email}
                </Text>
                <Text fontSize="sm" color={colors.textColor} noOfLines={1}>
                  <Text as="span" color={colors.mainColor} fontWeight="semibold">
                    {isArabic ? "النطاق: " : "Domain: "}
                  </Text>
                  {tenant.Domain}
                </Text>
              </Box>
            </>
          )}
        />
      </UnifiedCard>

      <UnifiedCard
        mt={10}
        mb={{ base: 20, md: 10 }}
        pb={{ base: 4, md: 0 }}
        title={isArabic ? "شركات التوصيل المتاحة" : "Available Delivery Companies"}
      >
        <ResponsiveTable
          columns={[
            { header: isArabic ? "اسم الشركة" : "Company Name", sortKey: "name" },
            { header: isArabic ? "البريد الإلكتروني" : "Email", sortKey: "email" },
            { header: isArabic ? "الحالة" : "Status", sortKey: "active" },
            { header: isArabic ? "التحكم" : "Action" },
          ]}
          data={[
            { name: "FastDelivery", email: "info@fastdelivery.com", active: true },
            { name: "QuickShip", email: "support@quickship.com", active: false },
          ]}
          searchable={true}
          sortable={true}
          renderRow={(company) => (
            <>
              <Td color={colors.textColor} fontWeight="medium">{company.name}</Td>
              <Td color={colors.textColor}>{company.email}</Td>
              <Td>
                <Badge
                  bg={company.active ? colors.badgeSuccess : colors.badgeError}
                  color={company.active ? colors.badgeSuccessText : colors.badgeErrorText}
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontWeight="semibold"
                >
                  {company.active
                    ? isArabic ? "مفعلة" : "Active"
                    : isArabic ? "غير مفعلة" : "Inactive"}
                </Badge>
              </Td>
              <Td>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    bg={colors.buttonBg}
                    color={colors.buttonText}
                    _hover={{ bg: colors.buttonHover }}
                    onClick={() => { }}
                  >
                    {isArabic ? "عرض" : "View"}
                  </Button>
                  <Button
                    size="sm"
                    bg={company.active ? "red.500" : "green.500"}
                    color="white"
                    _hover={{ opacity: 0.8 }}
                    onClick={() => { }}
                  >
                    {company.active
                      ? isArabic ? "إيقاف" : "Deactivate"
                      : isArabic ? "تفعيل" : "Activate"}
                  </Button>
                </HStack>
              </Td>
            </>
          )}
          renderCard={(company) => (
            <>
              <HStack justify="space-between" mb={3}>
                <HStack>
                  <Icon as={FaWallet} color={colors.mainColor} boxSize={5} />
                  <Text fontWeight="bold" fontSize="lg" color={colors.textColor}>
                    {company.name}
                  </Text>
                </HStack>
                <Badge
                  bg={company.active ? colors.badgeSuccess : colors.badgeError}
                  color={company.active ? colors.badgeSuccessText : colors.badgeErrorText}
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontWeight="semibold"
                >
                  {company.active
                    ? isArabic ? "مفعلة" : "Active"
                    : isArabic ? "غير مفعلة" : "Inactive"}
                </Badge>
              </HStack>

              <Divider my={3} borderColor={colors.dividerColor} />

              <VStack align="start" spacing={2} mb={4}>
                <Text color={colors.textColor} fontSize="sm">
                  <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                    {isArabic ? "البريد الإلكتروني: " : "Email: "}
                  </Text>
                  {company.email}
                </Text>
              </VStack>

              <HStack spacing={2}>
                <Button
                  size="sm"
                  bg={colors.buttonBg}
                  color={colors.buttonText}
                  _hover={{ bg: colors.buttonHover }}
                  flex={1}
                  onClick={() => { }}
                >
                  {isArabic ? "عرض" : "View"}
                </Button>
                <Button
                  size="sm"
                  bg={company.active ? "red.500" : "green.500"}
                  color="white"
                  _hover={{ opacity: 0.8 }}
                  flex={1}
                  onClick={() => { }}
                >
                  {company.active
                    ? isArabic ? "إيقاف" : "Deactivate"
                    : isArabic ? "تفعيل" : "Activate"}
                </Button>
              </HStack>
            </>
          )}
        />
      </UnifiedCard>
    </PageContainer>
  );
} 